import {
  DEFAULT_GRIND,
  DEFAULT_WEIGHT,
  GRINDS,
  type GrindKey,
  labelOf,
  unitPrice,
  WEIGHTS,
  type WeightKey,
} from '@c214/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OptionPicker } from '../components/product/OptionPicker';
import { PackArt } from '../components/product/PackArt';
import { StickyBuyBar } from '../components/product/StickyBuyBar';
import { TasteProfile } from '../components/product/TasteProfile';
import { HeartButton } from '../components/ui/heart-button';
import { useProduct } from '../hooks/useProducts';
import { useCart, useFavorites } from '../providers';

export function ProductPage() {
  const { id } = useParams();
  const productId = Number(id);
  const { data: product, isLoading, isError, error } = useProduct(productId);
  const cart = useCart();
  const favorites = useFavorites();

  const [weight, setWeight] = useState<WeightKey>(DEFAULT_WEIGHT);
  const [grind, setGrind] = useState<GrindKey>(DEFAULT_GRIND);
  const [qty, setQty] = useState(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: productId intentionally keys the reset so navigating to a different product restores the defaults.
  useEffect(() => {
    setWeight(DEFAULT_WEIGHT);
    setGrind(DEFAULT_GRIND);
    setQty(1);
  }, [productId]);

  if (isLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;
  if (isError || !product)
    return (
      <p className="section-pad text-[15px] text-muted">{error?.message ?? 'Товар не знайдено'}</p>
    );

  const price = unitPrice(product.price, weight);
  const spec = `${labelOf(WEIGHTS, weight)} · ${labelOf(GRINDS, grind)}`;

  return (
    <>
      <div className="section-pad grid gap-10 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
        <div
          className="flex items-center justify-center bg-surface p-8"
          style={{ minHeight: 'min(70vh,520px)' }}
        >
          <PackArt
            name={product.name}
            packSubtitle={product.packSubtitle}
            roast={product.roast}
            notes={product.notes}
            roastedAt={product.roastedAt}
            weight={weight}
            className="h-[min(70vh,520px)] w-auto max-w-full"
          />
        </div>

        <div>
          <Link to="/catalog" className="mono-label no-underline">
            ← КАТАЛОГ
          </Link>

          <p className="mono-label mt-6">
            {product.region} · {product.altitude} ·{' '}
            <span className="text-accent">ОЦІНКА {product.score}</span>
          </p>

          <h1 className="m-0 mt-3 text-[clamp(34px,5.5vw,64px)] font-extrabold leading-[0.95] tracking-[-0.045em]">
            {product.name}
          </h1>

          <p className="mono-label mt-3">
            ОБСМАЖЕНО {product.roastedAt.slice(8)}.{product.roastedAt.slice(5, 7)} · ВІДПРАВКА{' '}
            {product.shipsAt.slice(8)}.{product.shipsAt.slice(5, 7)}
          </p>

          <p className="mt-6 max-w-[46ch] text-[16px] leading-[1.6] text-muted">
            {product.description}
          </p>

          <div className="mt-8">
            <TasteProfile
              acid={product.acid}
              bitter={product.bitter}
              body={product.body}
              productId={product.id}
            />
          </div>

          <p className="mt-6 text-[15px] text-muted">Ноти: {product.notes}</p>

          <div className="mt-8 space-y-6">
            <OptionPicker label="Вага" options={WEIGHTS} value={weight} onChange={setWeight} />
            <OptionPicker label="Помел" options={GRINDS} value={grind} onChange={setGrind} />
          </div>
        </div>
      </div>

      <StickyBuyBar
        name={product.name}
        spec={spec}
        total={price * qty}
        qty={qty}
        onQtyChange={setQty}
        heart={
          <HeartButton
            active={favorites.has(product.id)}
            onToggle={() => favorites.toggle(product.id)}
          />
        }
        onAdd={() =>
          cart.add({
            productId: product.id,
            name: product.name,
            weight,
            grind,
            unitPrice: price,
            qty,
          })
        }
      />
    </>
  );
}
